import { ExistingReportPicker } from "@/components/common/existing-report-picker";
import { ModalWrapper } from "@/components/modals/modal-wrapper";
import queries from "@/queries";
import { popModal } from "@/stores/modal";
import { Button } from "@/toggle-ui-kit/button";
import {
  formatUploadedAt,
  getReportMetaLabel,
} from "@/utils/dsd-editor/report-list";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { DSD_REVIEW_COPY } from "./copy";
import { DSD_EDITOR_COPY } from "./dsd-editor-copy";
import { useInheritDsdEditorMappingReviews } from "./use-mapping-review";

/**
 * 검토 내용을 가져올 DSD 선택 모달 (베타 PoC). 지금 문서는 후보에서 뺀다.
 * 실패하면 모달을 열어 둔다 — 다른 DSD를 골라 다시 시도할 수 있다.
 */
export const DsdReviewImportModal = ({
  documentId,
  filename,
  reviewCount,
}: {
  documentId: string;
  filename: string;
  /** 이 문서에 이미 저장된 검토 수 */
  reviewCount: number;
}) => {
  const copy = DSD_REVIEW_COPY.reviewImport;
  const missingFilename =
    DSD_EDITOR_COPY.sources.existingReport.picker.missingFilename;
  const [sourceReportId, setSourceReportId] = useState<string | null>(null);
  const reportsQuery = useQuery(queries.dsdReviewImportSources());
  const reports = (reportsQuery.data?.reports ?? []).filter(
    (report) => report.reportId !== documentId,
  );
  const sourceReviewsQuery = useQuery({
    ...queries.dsdEditor(sourceReportId ?? "")._ctx.mappingReviews(),
    enabled: sourceReportId !== null,
  });
  const sourceReviewCount = sourceReviewsQuery.data?.reviews.length ?? 0;
  const inherit = useInheritDsdEditorMappingReviews(documentId);
  const submit = () => {
    if (sourceReportId) inherit.mutate(sourceReportId, { onSuccess: popModal });
  };

  return (
    <ModalWrapper className="flex max-h-[calc(100vh-48px)] w-[560px] flex-col gap-4 overflow-y-auto rounded-[12px] bg-background-base p-6">
      <div>
        <h2 className="headline-2-semibold text-label-normal">{copy.title}</h2>
        <p className="mt-1 label-2-regular text-label-alternative">
          {copy.subtitle}
        </p>
      </div>

      <div className="flex items-center gap-3 rounded-[8px] bg-background-neutral-alternative px-4 py-3">
        <span className="label-2-medium shrink-0 text-label-alternative">
          {copy.currentDocumentLabel}
        </span>
        <span className="label-1-normal-medium min-w-0 flex-1 truncate text-label-normal">
          {filename || missingFilename}
        </span>
        <span className="label-2-medium shrink-0 text-label-normal">
          {copy.reviewCount(reviewCount)}
        </span>
      </div>

      <ExistingReportPicker
        title={copy.picker.title}
        description={copy.picker.description}
        reports={reports}
        selectedReportId={sourceReportId}
        onSelect={setSourceReportId}
        getReportId={(report) => report.reportId}
        getSearchText={(report) =>
          `${report.filename} ${getReportMetaLabel(report)}`
        }
        searchPlaceholder={copy.picker.searchPlaceholder}
        showSelectionIndicator
        selectedTone="blue"
        listHeight="tall"
        renderReportTitle={(report) => report.filename || missingFilename}
        renderReportMeta={(report) =>
          [
            getReportMetaLabel(report),
            `${DSD_EDITOR_COPY.sources.existingReport.meta.uploadedAtPrefix} ${formatUploadedAt(report.uploadedAt)}`,
          ]
            .filter(Boolean)
            .join(" · ")
        }
        // 안내 줄은 min-h-7 — 실패 상태의 다시 시도 버튼과 높이를 맞춰 상태가 바뀌어도 모달이 움직이지 않는다
        selectedReportPlaceholder={
          <p className="label-2-medium flex min-h-7 items-center text-label-alternative">
            {copy.sourceReviews.noSelection}
          </p>
        }
        renderSelectedReport={() =>
          sourceReviewsQuery.isError ? (
            <div className="flex items-center justify-between gap-2">
              <p className="label-2-medium text-status-negative">
                {copy.sourceReviews.error}
              </p>
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                onClick={() => sourceReviewsQuery.refetch()}
              >
                {copy.sourceReviews.retry}
              </Button>
            </div>
          ) : (
            <p className="label-2-medium flex min-h-7 items-center text-label-normal">
              {sourceReviewsQuery.isLoading
                ? copy.sourceReviews.loading
                : sourceReviewCount > 0
                  ? copy.sourceReviews.ready(sourceReviewCount)
                  : copy.sourceReviews.empty}
            </p>
          )
        }
        isLoading={reportsQuery.isLoading}
        isError={reportsQuery.isError}
        emptyMessage={copy.picker.emptyMessage}
        errorMessage={copy.picker.errorMessage}
        onRetry={() => reportsQuery.refetch()}
      />

      <div className="flex justify-end gap-2">
        <Button
          variant="outlined"
          color="secondary"
          size={40}
          disabled={inherit.isPending}
          onClick={popModal}
        >
          {copy.cancel}
        </Button>
        <Button
          variant="solid"
          color="blue"
          size={40}
          disabled={sourceReviewCount === 0 || inherit.isPending}
          onClick={submit}
        >
          {inherit.isPending ? copy.pending : copy.submit}
        </Button>
      </div>
    </ModalWrapper>
  );
};
